import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
} from '@mui/joy';
import {
  ChartBar as Assessment,
  Bank as AccountBalance,
  TrendUp as TrendingUp,
  CurrencyDollar as AttachMoney,
  Receipt,
  ChartLine as ShowChart,
  ChartPie as PieChart,
  ChartBar as BarChart,
  ChartLine as Timeline,
  TreeStructure as AccountTree,
  Gauge as Speed,
  ChartLine as Analytics,
} from '@phosphor-icons/react';

interface ReportCategory {
  title: string;
  description: string;
  icon: React.ReactNode;
  reports: {
    name: string;
    description: string;
    path: string;
    icon: React.ReactNode;
  }[];
}

export const Reports: React.FC = () => {


  const reportCategories: ReportCategory[] = [
    {
      title: 'Account Details',
      description: 'Detailed account transaction reports',
      icon: <AccountTree />,
      reports: [
        {
          name: 'General Ledger',
          description: 'All transactions by account',
          path: '/accounting/reports/general-ledger',
          icon: <Receipt />,
        },
        {
          name: 'Account Activity',
          description: 'Detailed activity for specific accounts',
          path: '/accounting/reports/account-activity',
          icon: <Timeline />,
        },
        {
          name: 'Journal Entry Report',
          description: 'List of all journal entries',
          path: '/accounting/reports/journal-entries',
          icon: <Assessment />,
        },
      ],
    },
    {
      title: 'Financial Statements',
      description: 'Core financial reports for your business',
      icon: <Assessment />,
      reports: [
        {
          name: 'Balance Sheet',
          description: 'Assets, liabilities, and equity snapshot',
          path: '/accounting/reports/balance-sheet',
          icon: <AccountBalance />,
        },
        {
          name: 'Profit & Loss',
          description: 'Revenue and expense summary',
          path: '/accounting/reports/profit-loss',
          icon: <TrendingUp />,
        },
        {
          name: 'Cash Flow Statement',
          description: 'Cash inflows and outflows',
          path: '/accounting/reports/cash-flow',
          icon: <AttachMoney />,
        },
        {
          name: 'Trial Balance',
          description: 'Account balances verification',
          path: '/accounting/reports/trial-balance',
          icon: <Receipt />,
        },
      ],
    },
    {
      title: 'Receivables & Payables',
      description: 'Track money owed to and by your business',
      icon: <ShowChart />,
      reports: [
        {
          name: 'A/R Aging Report',
          description: 'Outstanding customer invoices by age',
          path: '/accounting/reports/ar-aging',
          icon: <Timeline />,
        },
        {
          name: 'A/P Aging Report',
          description: 'Outstanding vendor bills by age',
          path: '/accounting/reports/ap-aging',
          icon: <BarChart />,
        },
        {
          name: 'Customer Balances',
          description: 'Summary of customer account balances',
          path: '/accounting/reports/customer-balances',
          icon: <PieChart />,
        },
      ],
    },
    {
      title: 'Analytics & KPIs',
      description: 'Financial metrics and performance indicators',
      icon: <Analytics />,
      reports: [
        {
          name: 'Financial Analytics',
          description: 'Comprehensive financial ratios and KPIs',
          path: '/accounting/analytics',
          icon: <Speed />,
        },
      ],
    },
  ];

  return (
    <Box>
      <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
        Financial Reports
      </Typography>

      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>Choose a report to understand your balances, profitability, or outstanding invoices.</Typography>
      <Grid container spacing={2}>
        {reportCategories.map((category, index) => (
          <Grid key={index} xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {category.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography level="title-lg">{category.title}</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {category.description}
                    </Typography>
                  </Box>
                </Box>

                <List size="sm" sx={{ mx: -2 }}>
                  {category.reports.map((report, reportIndex) => (
                    <ListItem key={reportIndex}>
                      <ListItemButton
                        component={Link} to={report.path}
                        sx={{
                          borderRadius: 'sm',
                          '&:hover': {
                            backgroundColor: 'background.level1',
                          },
                        }}
                      >
                        <ListItemDecorator sx={{ color: 'primary.main' }}>
                          {report.icon}
                        </ListItemDecorator>
                        <Box>
                          <Typography level="body-md">{report.name}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {report.description}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Stats */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Report Tips
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            • Financial statements provide a comprehensive view of your business health
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            • Run A/R Aging reports weekly to stay on top of collections
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            • Compare periods to identify trends and seasonal patterns
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            • Export reports to Excel for additional analysis and sharing
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
